import type { MailComposerSource } from 'modules/Mails/types/mail-composer-source';
import type { UpsellFeature } from 'modules/Upsell/types';
import type { PostHog } from 'posthog-js';

export type AnalyticsTrigger = 'button' | 'shortcut';

export type MailActionOptions = { trigger: AnalyticsTrigger };

type AnalyticsEvents = {
  'upsell:page_viewed': { feature_name: UpsellFeature };
  'upsell:upgrade_clicked': { feature_name: UpsellFeature };
  'upsell:callout_clicked': { feature_name: UpsellFeature };
  'collection:invoices_treated': {
    action_type: string;
    invoices_treated_count: number;
  };
  'debtor:upsell_ai_context_provider_clicked': { debtor_id: string };
  'views:batch_action_executed': {
    action_type: string;
    selected_count: number;
    is_all_selected: boolean;
    page: string;
  };
  'views:export_downloaded': { resource_type: string; with_filters: boolean };
  'reports:export_downloaded': { format: 'xlsx' | 'csv' };
  'mail:composer_opened': {
    source: MailComposerSource;
    trigger: AnalyticsTrigger;
  };
  'mail:draft_created': { source: MailComposerSource };
  'mail:draft_discarded': { was_ai_assisted: boolean };
  'mail:ai_draft_generated': {
    status: 'success' | 'error';
    prompt_length: number;
  };
  'mail:sent': {
    source: MailComposerSource;
    recipients_count: number;
    has_attachments: boolean;
    is_ai_assisted: boolean;
  };
  'mail:thread_status_toggled': { is_processed: boolean; trigger: AnalyticsTrigger };
  'mail:thread_assign_opened': { trigger: AnalyticsTrigger };
  'mail:thread_navigated': { direction: 'prev' | 'next'; trigger: AnalyticsTrigger };
  'group:tour_started': { trigger: 'auto' | 'button' };
  'group:tour_closed': { step: number; steps_count: number };
  'group:tour_completed': { steps_count: number };
};

export type AnalyticsEventName = keyof AnalyticsEvents;

export const captureEvent = <TName extends AnalyticsEventName>(
  posthog: Pick<PostHog, 'capture'>,
  event: TName,
  properties: AnalyticsEvents[TName],
): void => {
  posthog.capture(event, properties);
};
